/**
 * Страница управления карьерными переходами
 * Визуальный редактор для создания и редактирования карьерных путей
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  GitBranch,
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  Filter,
  Search,
  Save,
  X,
  AlertCircle,
  RefreshCw,
  Zap,
  TrendingUp,
  ArrowUpRight,
  MoreHorizontal
} from 'lucide-react'
import { dataService } from '../../services/dataService'
import { logAction, ActionTypes } from '../utils/auditLogger'
import { 
  TransitionType,
  getTransitionTypeLabel,
  getTransitionTypeClasses,
  normalizeTransitionType,
  TRANSITION_TYPE_LABELS,
  ALL_TRANSITION_TYPES
} from '../../models/TransitionType'
import { 
  createTransitionStats,
  filterTransitionsByType,
  validateTransition 
} from '../../utils/transitionHelpers'

// Компонент карточки перехода
const TransitionCard = ({ transition, onEdit, onDelete }) => {
  const getTransitionTypeIcon = (type) => {
    const normalizedType = normalizeTransitionType(type)
    switch (normalizedType) {
      case TransitionType.VERTICAL:
        return <TrendingUp className="w-4 h-4 text-green-600" />
      case TransitionType.HORIZONTAL:
        return <ArrowRight className="w-4 h-4 text-blue-600" />
      case TransitionType.CROSS_FUNCTIONAL:
        return <ArrowUpRight className="w-4 h-4 text-orange-600" />
      default:
        return <GitBranch className="w-4 h-4 text-gray-600" />
    }
  }

  const getTransitionTypeColor = (type) => {
    const classes = getTransitionTypeClasses(type)
    return `${classes.bg} ${classes.border} ${classes.text}`
  }

  const getTransitionTypeName = (type) => {
    return getTransitionTypeLabel(type)
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getTransitionTypeIcon(transition.transition_type)}
          <span className={`px-2 py-1 text-xs font-medium border rounded-full ${getTransitionTypeColor(transition.transition_type)}`}>
            {getTransitionTypeName(transition.transition_type)}
          </span>
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onEdit(transition)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(transition.id)}
            className="text-gray-400 hover:text-red-600 p-1"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
            {transition.from_position_title || 'Неизвестная должность'}
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
            {transition.to_position_title || 'Неизвестная должность'}
          </div>
        </div>

        {transition.probability !== undefined && transition.probability < 100 && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Zap className="w-3 h-3" />
            <span>Вероятность: {Math.round(transition.probability)}%</span>
          </div>
        )}

        {transition.requirements && (
          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
            <strong>Требования:</strong> {transition.requirements}
          </div>
        )}
      </div>
    </motion.div>
  )
}

// Модальное окно для редактирования перехода
const TransitionModal = ({ transition, positions, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    from_position_id: '',
    to_position_id: '',
    transition_type: TRANSITION_TYPE_LABELS[TransitionType.VERTICAL],
    requirements: '',
    probability: 100
  })

  useEffect(() => {
    if (transition) {
      setFormData({
        from_position_id: transition.from_position_id?.toString() || '',
        to_position_id: transition.to_position_id?.toString() || '',
        transition_type: getTransitionTypeLabel(transition.transition_type),
        requirements: transition.requirements || '',
        probability: transition.probability ?? 100
      })
    } else {
      setFormData({
        from_position_id: '',
        to_position_id: '',
        transition_type: TRANSITION_TYPE_LABELS[TransitionType.VERTICAL],
        requirements: '',
        probability: 100
      })
    }
  }, [transition, isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  if (!isOpen) return null

  const fromPosition = positions.find(p => p.id === formData.from_position_id)
  const toPosition = positions.find(p => p.id === formData.to_position_id)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg w-full max-w-lg mx-4"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {transition ? 'Редактировать переход' : 'Создать переход'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Из должности
              </label>
              <select
                value={formData.from_position_id}
                onChange={(e) => setFormData({ ...formData, from_position_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Выберите должность</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>
                    {pos.title} ({pos.department_name || 'Неизвестный отдел'}, уровень {pos.level})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                В должность
              </label>
              <select
                value={formData.to_position_id}
                onChange={(e) => setFormData({ ...formData, to_position_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Выберите должность</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>
                    {pos.title} ({pos.department_name || 'Неизвестный отдел'}, уровень {pos.level})
                  </option>
                ))}
              </select>
            </div>

            {/* Предварительный просмотр перехода */}
            {fromPosition && toPosition && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {fromPosition.title}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <div className="bg-green-100 text-green-800 px-2 py-1 rounded">
                    {toPosition.title}
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  {fromPosition.department_name || 'Неизвестный отдел'} (уровень {fromPosition.level}) → {toPosition.department_name || 'Неизвестный отдел'} (уровень {toPosition.level})
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Тип перехода
              </label>
              <select
                value={formData.transition_type}
                onChange={(e) => setFormData({ ...formData, transition_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.values(TRANSITION_TYPE_LABELS).map(label => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Вероятность перехода
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.probability}
                  onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value, 10) })}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-12 text-center">
                  {formData.probability}%
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Требования для перехода
              </label>
              <textarea
                value={formData.requirements}
                onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Опыт, навыки, образование и т.д."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Сохранить</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

// Основной компонент страницы
const TransitionsPage = () => {
  const [transitions, setTransitions] = useState([])
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Фильтры и поиск
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')

  // Модальное окно
  const [transitionModal, setTransitionModal] = useState({ isOpen: false, transition: null })

  // Статистика (используем централизованную функцию createTransitionStats)
  const [stats, setStats] = useState(() => createTransitionStats([]))

  // Загрузка данных
  const loadData = async (isMounted = () => true) => {
    try {
      setLoading(true)
      
      // Используем новый централизованный метод ожидания
      await dataService.waitForInitialization()
      
      if (!isMounted()) return // Проверка что компонент не размонтирован

      // Читаем данные из серверного API через dataService
      const trans = await dataService.getTransitions()
      const pos = await dataService.getPositions()
      
      if (!isMounted()) return // Финальная проверка перед setState
      
      setTransitions(trans)
      setPositions(pos)

      // Подсчет статистики с использованием централизованных утилит
      const newStats = createTransitionStats(trans)
      setStats(newStats)

      setError(null)
    } catch (err) {
      console.error('Ошибка загрузки данных:', err)
      
      if (!isMounted()) return
      
      // Более детальная обработка ошибок
      if (err.message.includes('таймаут')) {
        setError('Таймаут загрузки данных. Проверьте соединение.')
      } else if (err.message.includes('404')) {
        setError('Данные не найдены на сервере')
      } else {
        setError('Ошибка загрузки данных: ' + err.message)
      }
    } finally {
      if (isMounted()) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    let isMounted = true
    
    const loadDataSafe = () => loadData(() => isMounted)
    loadDataSafe()
    
    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [])

  // Фильтрация переходов
  const filteredTransitions = transitions.filter(transition => {
    const matchesSearch = searchTerm === '' || 
      transition.from_position_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transition.to_position_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transition.requirements?.toLowerCase().includes(searchTerm.toLowerCase())

    // Используем централизованную функцию фильтрации по типу
    const typeFilteredTransitions = filterTransitionsByType([transition], typeFilter)
    const matchesType = typeFilter === 'all' || typeFilteredTransitions.length > 0

    const matchesDepartment = departmentFilter === 'all' || 
      transition.from_department_name === departmentFilter ||
      transition.to_department_name === departmentFilter

    return matchesSearch && matchesType && matchesDepartment
  })

  // Получение уникальных отделов для фильтра
  const departments = [...new Set([
    ...transitions.map(t => t.from_department_name),
    ...transitions.map(t => t.to_department_name)
  ])].filter(Boolean)

  // Обработчики
  const handleCreateTransition = () => {
    setTransitionModal({ isOpen: true, transition: null })
  }

  const handleEditTransition = (transition) => {
    setTransitionModal({ isOpen: true, transition })
  }

  const handleSaveTransition = async (formData) => {
    try {
      if (transitionModal.transition) {
        // Обновление
        const result = await dataService.updateTransition(transitionModal.transition.id, formData)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setTransitionModal({ isOpen: false, transition: null })
        } else {
          setError(result.error)
        }
      } else {
        // Создание
        const result = await dataService.createTransition(formData)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setTransitionModal({ isOpen: false, transition: null })
        } else {
          setError(result.error)
        }
      }
    } catch (err) {
      console.error('Ошибка сохранения перехода:', err)
      setError('Ошибка сохранения перехода')
    }
  }

  const handleDeleteTransition = async (transitionId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот переход?')) {
      try {
        const result = await dataService.deleteTransition(transitionId)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
        } else {
          setError(result.error)
        }
      } catch (err) {
        console.error('Ошибка удаления перехода:', err)
        setError('Ошибка удаления перехода')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Загрузка данных...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Карьерные переходы</h1>
          <p className="text-gray-600">Управление возможностями карьерного роста</p>
        </div>

        <button
          onClick={handleCreateTransition}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Создать переход</span>
        </button>
      </div>

      {/* Ошибки */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2"
        >
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Статистика */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <GitBranch className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">{stats.total.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total.count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">{stats.vertical.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.vertical.count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <ArrowRight className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">{stats.horizontal.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.horizontal.count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <ArrowUpRight className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-500">{stats.crossFunctional.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.crossFunctional.count}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Фильтры и поиск */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск по должностям..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                searchTerm ? 'pr-10' : 'pr-4'
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Очистить поиск"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Все типы</option>
            {Object.values(TRANSITION_TYPE_LABELS).map(label => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Все отделы</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
            <span>Найдено: {filteredTransitions.length}</span>
          </div>
        </div>
      </div>

      {/* Список переходов */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AnimatePresence>
          {filteredTransitions.map((transition) => (
            <TransitionCard
              key={transition.id}
              transition={transition}
              onEdit={handleEditTransition}
              onDelete={handleDeleteTransition}
            />
          ))}
        </AnimatePresence>
      </div>

      {filteredTransitions.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg border">
          <GitBranch className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {transitions.length === 0 ? 'Нет переходов' : 'Переходы не найдены'}
          </h3>
          <p className="text-gray-500 mb-4">
            {transitions.length === 0 
              ? 'Создайте первый карьерный переход для начала работы'
              : 'Попробуйте изменить параметры поиска или фильтры'
            }
          </p>
          {transitions.length === 0 && (
            <button
              onClick={handleCreateTransition}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Создать первый переход</span>
            </button>
          )}
        </div>
      )}

      {/* Модальное окно */}
      <AnimatePresence>
        <TransitionModal
          transition={transitionModal.transition}
          positions={positions}
          isOpen={transitionModal.isOpen}
          onClose={() => setTransitionModal({ isOpen: false, transition: null })}
          onSave={handleSaveTransition}
        />
      </AnimatePresence>
    </div>
  )
}

export default TransitionsPage 